import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Eye, MessageSquare, BadgeCheck, Building2, Crown, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function DealerDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [dealer, setDealer] = useState(null);
  const [showRegister, setShowRegister] = useState(false);
  const [dealerForm, setDealerForm] = useState({ dealer_name: "", phone: "", telegram: "", address: "", description: "" });
  const [featuringId, setFeaturingId] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/login?next=/dealer-dashboard");
  }, [user, loading, navigate]);

  const refresh = async () => {
    if (!user || !user.dealer_id) return;
    const [a, e, d] = await Promise.all([
      api.get("/listings", { params: { dealer_id: user.dealer_id, status: "" } }),
      api.get("/enquiries"),
      api.get(`/dealers/${user.dealer_id}`),
    ]);
    setListings(a.data.items || []);
    setEnquiries(e.data || []);
    setDealer(d.data);
  };

  useEffect(() => {
    if (!user) return;
    if (user.role !== "dealer" || !user.dealer_id) { setShowRegister(true); return; }
    refresh();
  }, [user]);

  const registerDealer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/dealers", dealerForm);
      window.location.reload();
    } catch (err) {
      alert("Failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const featureListing = async (listingId) => {
    setFeaturingId(listingId);
    try {
      const { data } = await api.post("/payments/feature-listing", { listing_id: listingId });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.mocked) {
        toast.success("MOCKED payment success — listing featured for 30 days");
        refresh();
      }
    } catch (e) {
      toast.error("Payment failed: " + (e.response?.data?.detail || e.message));
    } finally { setFeaturingId(null); }
  };

  if (!user) return null;

  if (showRegister) {
    return (
      <div data-testid="dealer-register-page" className="bg-slate-50 min-h-screen flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg bg-white border border-slate-200 rounded-2xl p-7">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-cm-primary text-white flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <div>
              <h1 className="font-display font-extrabold text-xl text-cm-primary">Register as a Dealer</h1>
              <p className="text-xs text-slate-500">Get a verified profile and unlock dealer features</p>
            </div>
          </div>
          <form onSubmit={registerDealer} className="mt-5 space-y-3">
            <Field testid="dealer-name" label="Dealer name" value={dealerForm.dealer_name} onChange={(v) => setDealerForm({ ...dealerForm, dealer_name: v })} required />
            <Field testid="dealer-phone" label="Phone" value={dealerForm.phone} onChange={(v) => setDealerForm({ ...dealerForm, phone: v })} required />
            <Field testid="dealer-telegram" label="Telegram (e.g. @yourshop or +85512345678)" value={dealerForm.telegram} onChange={(v) => setDealerForm({ ...dealerForm, telegram: v })} />
            <Field testid="dealer-address" label="Address" value={dealerForm.address} onChange={(v) => setDealerForm({ ...dealerForm, address: v })} required />
            <label className="block">
              <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">Description</span>
              <textarea data-testid="dealer-description" rows={3} value={dealerForm.description} onChange={(e) => setDealerForm({ ...dealerForm, description: e.target.value })} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" />
            </label>
            <button data-testid="dealer-register-submit" type="submit" className="w-full px-5 py-3 rounded-xl bg-cm-primary text-white font-medium hover:bg-cm-accent">Register as dealer</button>
          </form>
        </div>
      </div>
    );
  }

  const stats = {
    total: listings.length,
    approved: listings.filter(l => l.status === "approved").length,
    pending: listings.filter(l => l.status === "pending").length,
    views: listings.reduce((a, l) => a + (l.views || 0), 0),
  };

  return (
    <div data-testid="dealer-dashboard-page" className="bg-slate-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 md:py-10">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h1 className="font-display font-extrabold text-2xl sm:text-3xl text-cm-primary">Dealer Dashboard</h1>
            <p className="text-sm text-slate-500 mt-1">Manage your inventory and enquiries</p>
          </div>
          <Link to="/sell" data-testid="dealer-add-listing" className="px-5 py-3 rounded-xl bg-cm-primary text-white font-medium flex items-center gap-2 press-scale hover:bg-cm-accent">
            <Plus className="w-4 h-4" /> Add listing
          </Link>
        </div>

        {/* Subscription card */}
        {dealer && (
          <div data-testid="subscription-card" className="mt-6 bg-gradient-to-br from-cm-primary to-cm-primary-deep text-white rounded-2xl p-5 md:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
              <Crown className="w-6 h-6 text-cm-accent" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-[0.2em] text-white/70">Current plan</div>
              <div className="font-display font-extrabold text-xl capitalize mt-0.5">
                {dealer.subscription_plan || "Basic"}
                {dealer.subscription_expires_at && (
                  <span className="ml-2 text-xs font-normal text-white/70">
                    expires {new Date(dealer.subscription_expires_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
            <Link to="/pricing" data-testid="upgrade-plan-btn" className="px-4 py-2.5 rounded-xl bg-cm-accent hover:bg-cm-accent-hover text-white text-sm font-semibold press-scale">
              Upgrade plan
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { k: "Total listings", v: stats.total },
            { k: "Approved", v: stats.approved },
            { k: "Pending", v: stats.pending },
            { k: "Total views", v: stats.views },
          ].map((s) => (
            <div key={s.k} className="bg-white border border-slate-200 rounded-2xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{s.k}</div>
              <div className="font-display font-extrabold text-2xl text-cm-primary mt-1">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Listings table */}
        <h2 className="mt-8 font-display font-bold text-xl text-cm-primary">My Listings</h2>
        <div className="mt-3 bg-white border border-slate-200 rounded-2xl overflow-hidden">
          {listings.length === 0 ? (
            <div className="p-10 text-center text-slate-500">No listings yet. <Link to="/sell" className="text-cm-accent font-medium">Create one →</Link></div>
          ) : (
            <div className="divide-y divide-slate-100">
              {listings.map((l) => (
                <div key={l.id} data-testid={`dealer-listing-${l.id}`} className="flex items-center gap-4 p-4 hover:bg-slate-50">
                  <Link to={`/listing/${l.id}`} className="w-20 h-14 rounded-lg bg-slate-100 overflow-hidden flex-shrink-0">
                    {l.images?.[0] && <img src={l.images[0]} alt="" className="w-full h-full object-cover" />}
                  </Link>
                  <Link to={`/listing/${l.id}`} className="flex-1 min-w-0">
                    <div className="font-semibold text-cm-primary truncate hover:underline">{l.year} {l.brand} {l.model}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{l.location} · {l.listing_type === 'rental' ? `$${l.daily_price}/day` : formatPrice(l.price)}</div>
                  </Link>
                  <div className="hidden sm:flex items-center gap-1 text-xs text-slate-500"><Eye className="w-3 h-3" /> {l.views || 0}</div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${l.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : l.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{l.status}</span>
                  {l.status === 'approved' && !l.featured && (
                    <button
                      onClick={() => featureListing(l.id)}
                      disabled={featuringId === l.id}
                      data-testid={`promote-${l.id}`}
                      className="hidden md:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-cm-accent text-white text-xs font-semibold hover:bg-cm-accent-hover disabled:opacity-60"
                    >
                      <Sparkles className="w-3 h-3" /> Promote $9.99
                    </button>
                  )}
                  {l.featured && (
                    <span className="hidden md:inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-100 text-amber-700 text-xs font-semibold">
                      <Star className="w-3 h-3 fill-amber-500" /> Featured
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Enquiries */}
        <h2 className="mt-8 font-display font-bold text-xl text-cm-primary">Recent Enquiries</h2>
        <div className="mt-3 bg-white border border-slate-200 rounded-2xl p-5">
          {enquiries.length === 0 ? (
            <p className="text-slate-500 text-sm">No enquiries yet.</p>
          ) : (
            <div className="space-y-3">
              {enquiries.slice(0, 10).map((e) => (
                <div key={e.id} className="border-b border-slate-100 pb-3 last:border-0">
                  <div className="flex items-center gap-2"><MessageSquare className="w-4 h-4 text-cm-accent" /><span className="font-semibold text-cm-primary text-sm">{e.user_name}</span><span className="text-xs text-slate-500">· {e.phone}</span></div>
                  <p className="text-sm text-slate-600 mt-1">{e.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5 font-semibold">{label}</span>
      <input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm focus:bg-white focus:border-cm-accent outline-none" />
    </label>
  );
}
